import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, User, FloppyDisk, Wallet } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const PUESTOS = [
  'ENCARGADO GENERAL',
  'GRANJERO',
  'ADM',
  'CHOFER',
  'CONTADOR',
  'SUPERVISOR',
  'AYUDANTE',
  'OTRO'
];

export default function EmpleadoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [formData, setFormData] = useState({
    nombre_completo: '',
    apodo: '',
    cedula: '',
    lote_id: '',
    lote_numero: '',
    proyecto: '',
    puesto: '',
    fecha_ingreso: new Date().toISOString().split('T')[0],
    sueldo_mensual: '',
    activo: true,
    // Deducciones fijas
    afp_fijo: '',
    ars_fijo: '',
    cxc_fijo: '',
    seg_medico_fijo: '',
  });

  useEffect(() => {
    loadLotes();
    if (isEdit) {
      loadEmpleado();
    }
  }, [id]);

  const loadLotes = async () => {
    try {
      const response = await lotesAPI.getAll();
      setLotes(response.data);
    } catch (error) {
      console.error('Error loading lotes');
    }
  };

  const loadEmpleado = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_BASE}/empleados/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const emp = response.data;
      setFormData({
        nombre_completo: emp.nombre_completo || '',
        apodo: emp.apodo || '',
        cedula: emp.cedula || '',
        lote_id: emp.lote_id || '',
        lote_numero: emp.lote_numero || '',
        proyecto: emp.proyecto || '',
        puesto: emp.puesto || '',
        fecha_ingreso: emp.fecha_ingreso ? emp.fecha_ingreso.split('T')[0] : '',
        sueldo_mensual: emp.sueldo_mensual?.toString() || '',
        activo: emp.activo !== false,
        afp_fijo: emp.afp_fijo?.toString() || '',
        ars_fijo: emp.ars_fijo?.toString() || '',
        cxc_fijo: emp.cxc_fijo?.toString() || '',
        seg_medico_fijo: emp.seg_medico_fijo?.toString() || '',
      });
    } catch (error) {
      toast.error('Error al cargar empleado');
      navigate('/nomina');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_completo || !formData.puesto || !formData.sueldo_mensual) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const payload = {
        nombre_completo: formData.nombre_completo,
        apodo: formData.apodo || null,
        cedula: formData.cedula || null,
        lote_id: formData.lote_id || null,
        lote_numero: formData.lote_numero || null,
        proyecto: formData.proyecto || 'GENERAL',
        puesto: formData.puesto,
        fecha_ingreso: formData.fecha_ingreso || null,  // Enviar como YYYY-MM-DD sin conversión a UTC
        sueldo_mensual: parseFloat(formData.sueldo_mensual),
        activo: formData.activo,
        afp_fijo: parseFloat(formData.afp_fijo) || 0,
        ars_fijo: parseFloat(formData.ars_fijo) || 0,
        cxc_fijo: parseFloat(formData.cxc_fijo) || 0,
        seg_medico_fijo: parseFloat(formData.seg_medico_fijo) || 0,
      };

      if (isEdit) {
        await axios.put(`${API_BASE}/empleados/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Empleado actualizado');
      } else {
        await axios.post(`${API_BASE}/empleados`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Empleado creado');
      }
      
      navigate('/nomina');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  // Calcular sueldo neto estimado
  const sueldoMensual = parseFloat(formData.sueldo_mensual) || 0;
  const totalDeducciones = (parseFloat(formData.afp_fijo) || 0) + 
                           (parseFloat(formData.ars_fijo) || 0) + 
                           (parseFloat(formData.cxc_fijo) || 0) + 
                           (parseFloat(formData.seg_medico_fijo) || 0);
  const sueldoNetoQuincenal = (sueldoMensual / 2) - (totalDeducciones / 2);

  if (loadingData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner h-8 w-8 border-2" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto animate-fade-in" data-testid="empleado-form">
        <div className="mb-6">
          <Link
            to="/nomina"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={14} />
            Volver a Nómina
          </Link>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {isEdit ? 'Editar Empleado' : 'Nuevo Empleado'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Personal */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User size={24} className="text-[#E73425]" weight="fill" />
                Información del Empleado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nombre y Apodo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre_completo">Nombre Completo *</Label>
                  <Input
                    id="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={(e) => handleChange('nombre_completo', e.target.value)}
                    placeholder="Nombre(s) y Apellido(s)"
                    data-testid="emp-nombre"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apodo">Apodo</Label>
                  <Input
                    id="apodo"
                    value={formData.apodo}
                    onChange={(e) => handleChange('apodo', e.target.value)}
                    placeholder="Apodo o sobrenombre"
                    data-testid="emp-apodo"
                  />
                </div>
              </div>

              {/* Cédula y Puesto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cedula">Cédula</Label>
                  <Input
                    id="cedula"
                    value={formData.cedula}
                    onChange={(e) => handleChange('cedula', e.target.value)}
                    placeholder="000-0000000-0"
                    data-testid="emp-cedula"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="puesto">Puesto *</Label>
                  <Select
                    value={formData.puesto}
                    onValueChange={(value) => handleChange('puesto', value)}
                  >
                    <SelectTrigger data-testid="emp-puesto">
                      <SelectValue placeholder="Seleccione puesto" />
                    </SelectTrigger>
                    <SelectContent>
                      {PUESTOS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Proyecto, Lote # y Lote Asignado */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="proyecto">Proyecto</Label>
                  <Input
                    id="proyecto"
                    value={formData.proyecto}
                    onChange={(e) => handleChange('proyecto', e.target.value)}
                    placeholder="GENERAL, CARLOS LANTIGUA..."
                    data-testid="emp-proyecto"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lote_numero">Lote #</Label>
                  <Input
                    id="lote_numero"
                    value={formData.lote_numero}
                    onChange={(e) => handleChange('lote_numero', e.target.value)}
                    placeholder="1, 2, 3, ALL..."
                    data-testid="emp-lote-numero"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lote_id">Lote Asignado</Label>
                  <Select
                    value={formData.lote_id}
                    onValueChange={(value) => handleChange('lote_id', value)}
                  >
                    <SelectTrigger data-testid="emp-lote">
                      <SelectValue placeholder="Opcional" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">TODOS</SelectItem>
                      {lotes.filter(l => l.estado === 'activo').map((lote) => (
                        <SelectItem key={lote.id} value={lote.id}>
                          {lote.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fecha y Sueldo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fecha_ingreso">Fecha de Ingreso</Label>
                  <Input
                    id="fecha_ingreso"
                    type="date"
                    value={formData.fecha_ingreso}
                    onChange={(e) => handleChange('fecha_ingreso', e.target.value)}
                    data-testid="emp-fecha"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sueldo_mensual">Sueldo Mensual ($) *</Label>
                  <Input
                    id="sueldo_mensual"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.sueldo_mensual}
                    onChange={(e) => handleChange('sueldo_mensual', e.target.value)}
                    placeholder="Ej: 19000"
                    data-testid="emp-sueldo"
                  />
                </div>
              </div>

              {/* Estado activo */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-border/60">
                <div>
                  <Label htmlFor="activo">Empleado Activo</Label>
                  <p className="text-sm text-muted-foreground">
                    Los empleados inactivos no aparecen en los pagos de nómina
                  </p>
                </div>
                <Switch
                  id="activo"
                  checked={formData.activo}
                  onCheckedChange={(checked) => handleChange('activo', checked)}
                  data-testid="emp-activo"
                />
              </div>
            </CardContent>
          </Card>

          {/* Deducciones Fijas */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet size={24} className="text-[#F69031]" weight="fill" />
                Deducciones Fijas (Mensual)
              </CardTitle>
              <CardDescription>
                Estas deducciones se aplicarán automáticamente en cada pago de nómina
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="afp_fijo">AFP</Label>
                  <Input
                    id="afp_fijo"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.afp_fijo}
                    onChange={(e) => handleChange('afp_fijo', e.target.value)}
                    placeholder="0.00"
                    data-testid="emp-afp"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ars_fijo">ARS</Label>
                  <Input
                    id="ars_fijo"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.ars_fijo}
                    onChange={(e) => handleChange('ars_fijo', e.target.value)}
                    placeholder="0.00"
                    data-testid="emp-ars"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cxc_fijo">CXC</Label>
                  <Input
                    id="cxc_fijo"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cxc_fijo}
                    onChange={(e) => handleChange('cxc_fijo', e.target.value)}
                    placeholder="0.00"
                    data-testid="emp-cxc"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seg_medico_fijo">Seg. Médico</Label>
                  <Input
                    id="seg_medico_fijo"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.seg_medico_fijo}
                    onChange={(e) => handleChange('seg_medico_fijo', e.target.value)}
                    placeholder="0.00"
                    data-testid="emp-segmedico"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resumen Preview */}
          {formData.sueldo_mensual && (
            <Card className="border-border/60 bg-gradient-to-r from-red-50 to-orange-50">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Sueldo Mensual</p>
                    <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(sueldoMensual)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Quincenal Base</p>
                    <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(sueldoMensual / 2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Deducciones Quinc.</p>
                    <p className="text-lg font-bold text-red-500" style={{ fontFamily: 'JetBrains Mono' }}>
                      -{formatCurrency(totalDeducciones / 2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Neto Quincenal</p>
                    <p className="text-lg font-bold text-[#E73425]" style={{ fontFamily: 'JetBrains Mono' }}>
                      {formatCurrency(sueldoNetoQuincenal)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/nomina')}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#E73425] hover:bg-[#D81C23]"
              disabled={loading}
              data-testid="submit-empleado"
            >
              {loading ? (
                <div className="spinner h-5 w-5" />
              ) : (
                <>
                  <FloppyDisk size={18} className="mr-2" />
                  {isEdit ? 'Guardar Cambios' : 'Crear Empleado'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
